import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  List,
  ListItem,
  Progress,
  Text,
  VStack,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import {
  collection,
  deleteField,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useState } from 'react';
import { toast } from 'react-toastify';
import firebaseFirestore from '~/config/firebase/firestore/firestore';
import { useAuth } from '~/hooks/useAuth';

type SedeAnalizada = {
  id: string;
  nombre: string;
  fechasPasadas: string[];
  totalFechas: number;
  pesoKb: number;
};

/**
 * Limpieza de la agenda: elimina del mapa de ocupación las fechas que ya
 * pasaron. Las reservas NO se borran; solo se libera el espacio que ocupan
 * los días vividos, que hace crecer el documento de cada sede sin límite.
 *
 * La pantalla exige haber iniciado sesión (ver el router) y actúa solo sobre
 * las sedes de la empresa de quien entra, salvo que sea superadmin.
 */
const DeleteReservations = () => {
  const { user } = useAuth();
  const [analizando, setAnalizando] = useState(false);
  const [limpiando, setLimpiando] = useState(false);
  const [sedes, setSedes] = useState<SedeAnalizada[]>();
  const [resultado, setResultado] = useState<string>();

  const esSuperadmin = user?.userRole?.name === 'superadmin';
  const hoy = format(new Date(), 'yyyy-MM-dd');

  const analizar = async () => {
    setAnalizando(true);
    setResultado(undefined);
    try {
      const sedesRef = collection(firebaseFirestore, 'locations');
      // Un administrador solo puede limpiar las sedes de su propia empresa.
      const consulta = esSuperadmin
        ? query(sedesRef)
        : query(
            sedesRef,
            where('company', '==', doc(firebaseFirestore, 'companies', user?.company?.id ?? ''))
          );

      const snapshot = await getDocs(consulta);
      const analizadas: SedeAnalizada[] = snapshot.docs.map((docSnap) => {
        const datos = docSnap.data();
        const reservations = datos.reservations ?? {};
        const fechas = Object.keys(reservations);
        return {
          id: docSnap.id,
          nombre: datos.name ?? '(sin nombre)',
          fechasPasadas: fechas.filter((fecha) => fecha < hoy).sort(),
          totalFechas: fechas.length,
          pesoKb: Math.round(JSON.stringify(datos).length / 1024),
        };
      });

      setSedes(analizadas);
    } catch (error) {
      toast.error('No se pudo analizar la agenda');
      console.error(error);
    } finally {
      setAnalizando(false);
    }
  };

  const limpiar = async () => {
    if (!sedes) return;
    const aLimpiar = sedes.filter((s) => s.fechasPasadas.length > 0);
    if (aLimpiar.length === 0) return;

    const total = aLimpiar.reduce((suma, s) => suma + s.fechasPasadas.length, 0);
    const confirmado = window.confirm(
      `Se eliminarán ${total} día(s) ya pasados de la agenda en ${aLimpiar.length} ` +
        'sede(s).\n\nLas reservas NO se borran: solo se libera el espacio de días ' +
        'que ya ocurrieron.\n\n¿Continuar?'
    );
    if (!confirmado) return;

    setLimpiando(true);
    let limpiadas = 0;
    let fallidas = 0;

    // De una en una y esperando cada escritura: antes se lanzaban todas a la
    // vez sin await y no se sabía si alguna había fallado.
    for (const sede of aLimpiar) {
      try {
        const cambios: Record<string, ReturnType<typeof deleteField>> = {};
        for (const fecha of sede.fechasPasadas) {
          cambios[`reservations.${fecha}`] = deleteField();
        }
        // Se tocan solo esas fechas, nunca el documento completo.
        await updateDoc(doc(firebaseFirestore, 'locations', sede.id), cambios);
        limpiadas += sede.fechasPasadas.length;
      } catch (error) {
        fallidas++;
        console.error(`Error limpiando ${sede.nombre}`, error);
      }
    }

    setLimpiando(false);
    setResultado(
      `${limpiadas} día(s) eliminados en ${aLimpiar.length - fallidas} sede(s).` +
        (fallidas ? ` ${fallidas} sede(s) con error.` : '')
    );
    toast.success('Agenda limpiada');
    await analizar();
  };

  const conFechasPasadas = sedes?.filter((s) => s.fechasPasadas.length > 0) ?? [];
  const totalDias = conFechasPasadas.reduce((s, x) => s + x.fechasPasadas.length, 0);

  return (
    <Flex justify={'center'} p={8}>
      <VStack align={'stretch'} maxW={'720px'} w={'full'} spacing={5}>
        <Box>
          <Heading size={'md'}>Limpieza de agenda</Heading>
          <Text color={'gray.600'} mt={1}>
            Elimina de la agenda los días que ya pasaron. Las reservas no se borran.
          </Text>
        </Box>

        <Alert status='info' borderRadius={'md'}>
          <AlertIcon />
          <Text fontSize={'sm'}>
            {esSuperadmin
              ? 'Como superadmin, se analizarán las sedes de todos los negocios.'
              : 'Se analizarán únicamente las sedes de su negocio.'}
          </Text>
        </Alert>

        <HStack>
          <Button onClick={analizar} isLoading={analizando} loadingText='Analizando'>
            Analizar agenda
          </Button>
          {conFechasPasadas.length > 0 && (
            <Button
              colorScheme='red'
              onClick={limpiar}
              isLoading={limpiando}
              loadingText='Limpiando'
            >
              Limpiar {totalDias} día(s)
            </Button>
          )}
        </HStack>

        {(analizando || limpiando) && <Progress size='xs' isIndeterminate />}

        {resultado && (
          <Alert status='success' borderRadius={'md'}>
            <AlertIcon />
            <Text fontSize={'sm'}>{resultado}</Text>
          </Alert>
        )}

        {sedes && (
          <Box borderWidth={'1px'} borderRadius={'md'} p={4}>
            <Text fontWeight={'bold'} mb={3}>
              {sedes.length} sede(s) analizadas
            </Text>
            {conFechasPasadas.length === 0 ? (
              <Text color={'green.600'}>No hay días pasados que limpiar.</Text>
            ) : (
              <List spacing={2}>
                {conFechasPasadas.map((sede) => (
                  <ListItem key={sede.id}>
                    <HStack justifyContent={'space-between'}>
                      <Text>{sede.nombre}</Text>
                      <HStack>
                        <Badge colorScheme='orange'>
                          {sede.fechasPasadas.length} día(s) pasados
                        </Badge>
                        <Badge colorScheme={sede.pesoKb > 700 ? 'red' : 'gray'}>
                          {sede.pesoKb} KB
                        </Badge>
                      </HStack>
                    </HStack>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </VStack>
    </Flex>
  );
};

export default DeleteReservations;
